import { forwardRef } from 'react';
import { cn } from '../../utils/cn';

/**
 * Input component with label, helper text, and error state.
 *
 * @param {Object} props
 * @param {string} [props.label] - Input label
 * @param {string} [props.helperText] - Helper text below input
 * @param {string} [props.error] - Error message (also sets error styling)
 * @param {React.ReactNode} [props.leadingIcon] - Icon at start of input
 * @param {React.ReactNode} [props.trailingIcon] - Icon at end of input
 * @param {string} [props.className] - Additional classes for wrapper
 * @param {string} [props.inputClassName] - Additional classes for input element
 */
export const Input = forwardRef(function Input(
  {
    label,
    helperText,
    error,
    leadingIcon,
    trailingIcon,
    className,
    inputClassName,
    id,
    ...props
  },
  ref
) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  const baseInputStyles = `
    w-full px-3 py-2 text-sm text-neutral-900 bg-white
    border rounded-md
    placeholder:text-neutral-500
    focus:outline-none focus:ring-2 focus:border-transparent
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50
    transition-colors duration-150
  `;

  const inputStateStyles = error
    ? 'border-error-500 focus:ring-error-500'
    : 'border-neutral-300 focus:ring-primary-500';

  const paddingStyles = cn(
    leadingIcon && 'pl-10',
    trailingIcon && 'pr-10'
  );

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leadingIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-neutral-500">
            {leadingIcon}
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            baseInputStyles,
            inputStateStyles,
            paddingStyles,
            inputClassName
          )}
          {...props}
        />
        {trailingIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-neutral-500">
            {trailingIcon}
          </div>
        )}
      </div>
      {(helperText || error) && (
        <p className={cn(
          'text-sm',
          error ? 'text-error-600' : 'text-neutral-500'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});
